/**
 * @description This model represents the mapped values of a product
 * @typedef Product
 * @type {object}
 * @property {ProductChildSku[]} childSkus
 * @property {Inventory} [inventoryProperties]
 */

/**
 * @typedef ProductChildSku
 * @type {object}
 * @property {string} productId
 * @property {string} skuId
 * @property {string} type
 * @property {number} skuQuantity
 * @property {string} availableChannels
 * @property {Inventory} [inventory]
 */

/**
 * Types common to Product model and Equinox Product model
 * @typedef Inventory
 * @type {object}
 * @property {number} atpQty
 * @property {boolean} backOrdered
 * @property {number} backOrderedQty
 */

/**
 * @description This model represents the error response.
 * @typedef EquinoxErrorResponse
 * @type {object}
 * @property {string} code e.g., SFOSER002
 * @property {string} message e.g., No Results Found dot Search Term  null
 * @property {number} timestamp e.g., 1679715447962
 */

/**
 * @description This model represents the response.
 * @typedef EquinoxResponse
 * @type {object}
 * @property {EquinoxNormalProduct[]} product
 */

/**
 * @description EquinoxNormalProduct represents an equinox Normal product type.
 * @typedef EquinoxNormalProduct
 * @type {object}
 * @property {string} identifier e.g., Normal - "blt8b757df6dcaa199c" | Kit - "02001076"
 * @property {EquinoxProductProperties} properties
 * @property {EquinoxProductVariant[]} sku or varaints - available only when product is a normal product.
 * @property {boolean} [exists] indicates whethere the SKU exists or not (HTTP 404) e.g., false
 */

/**
 * @description EquinoxKitProduct represents an equinox Kit product type.
 * @typedef EquinoxKitProduct
 * @type {object}
 * @property {"kit"|"bundle"} [type] available only when product is type of kit.
 */

/**
 * @typedef {(
 *  EquinoxProductCommonProperties & EquinoxNormalProductProperties
 * )} EquinoxProductProperties
 */

/**
 * @description These are the properties unique to Normal product type.
 * @typedef EquinoxNormalProductProperties
 * @type {object}
 * @property {string} [ingredients]
 * @property {string} [benefits]
 * @property {string} [usage]
 * @property {string} [resources]
 * @property {string} [scanQualifiedCount] e.g., "0"
 * @property {string} [status] e.g., "active"
 * @property {string} [size] e.g., "30 mL / 1 fl.oz"
 * @property {string} [productDetailsDescription] e.g., "[]"
 */

/**
 * @description These are the properties unique to Kit product type.
 * @typedef EquinoxKitProductProperties
 * @type {object}
 * @property {string} [skukits] e.g., "02002145~1,02002146~1,02002147~1,02002148~1,02002142~1,02002143~1"
 * @property {string} [productStatus] e.g., "Sellable"
 * @property {string} [availableChannels] e.g., "subscription,arsPhone,kiosk,mobile,web"
 * @property {string} [market] e.g., "CA"
 */

/**
 * @description These are the common properties between Normal and Kit product types
 * @typedef EquinoxProductCommonProperties
 * @type {object}
 * @property {string} [name] e.g., "180° Cell Renewal Fluid"
 * @property {string} [imageURL] e.g., "https://nuskin.com/content/dam/global/...ule-refreshed.jpg"
 * @property {string} [description] e.g., "180° Ampoule"
 * @property {string} [customerTypes] e.g., "BrandAffiliate,Preferred,Retail"
 * @property {string} [division] e.g., "Nu Skin"
 */

/**
 * @typedef EquinoxProductVariant
 * @type {object}
 * @property {string} [identifier] e.g., "02002145"
 * @property {string} [inventory] e.g., "IN STOCK"
 * @property {EquinoxProductVariantProperties} [properties]
 * @property {any[]} [promotion] e.g., ???
 * @property {EquinoxProductPriceFacets} [priceFacets]
 * @property {EquinoxProductInventoryProperties} [inventoryProperties]
 * @property {EquinxProductTotalValue} [totalValue]
 * @property {boolean} [default] For multi-variant products, this indicates that this is the searched SKU
 *  when using catalogs/search endpoint.
 */

/**
 * @description This does not contain the full list of properties from the response.
 * @typedef EquinoxProductVariantProperties
 * @type {object}
 * @property {string} [availableChannels] e.g., "subscription,arsPhone,kiosk,mobile,web"
 * @property {string} [imageURL] e.g., "https://nuskin.com/cont...21/ca-180-Ampoule-refreshed.jpg"
 * @property {string} [name] e.g., "180° Cell Renewal Fluid"
 * @property {string} [market] e.g., "CA"
 * @property {string} [description] e.g., "180° Ampoule"
 * @property {string} [ingredients]
 * @property {string} [benefits]
 * @property {string} [usage]
 * @property {string} [resources]
 * @property {string} [scanQualifiedCount] e.g., "0"
 * @property {string} [customerTypes] e.g., "BrandAffiliate,Preferred,Retail"
 * @property {string} [division] e.g., "Nu Skin"
 * @property {string} [status] e.g., "active"
 * @property {string} [size] e.g., "30 mL / 1 fl.oz"
 * @property {string} [globalProductId]
 * @property {string} [excludeFromSearch]
 * @property {string} [chargeShipping]
 * @property {string} [title]
 * @property {string} [productStatus] e.g., "Sellable"
 * @property {string} [variantLabel]
 * @property {string} [isExclusive] e.g., "false" - Why is this a string??
 */

/**
 * @description Why are properties not camelCase and type of string instead of number? Super weird!
 * @typedef EquinoxProductPriceFacets
 * @type {object}
 * @property {string} CV e.g., "76.0"
 * @property {string} Regular Price e.g., "76.0"
 * @property {string} PV e.g., "54.15"
 * @property {string} Wholesale Price e.g., "88.0"
 * @property {string} SB e.g., "4.25"
 */

/**
 * @description
 * @typedef EquinoxProductInventoryProperties
 * @type {object}
 * @property {boolean} [available] e.g., true
 * @property {boolean} [lowStock] e.g., false
 * @property {string} [binName] e.g., "1046_CA01"
 * @property {boolean} [backOrdered] e.g., false
 * @property {number} [expectedBackOrderAvailabilityDate] e.g., 0
 * @property {boolean} [preOrdered] e.g., false
 * @property {number} [expectedPreOrderAvailabilityDate] e.g., 0
 * @property {number} [atpQty] e.g., 49882
 * @property {number} [preOrderedQty] e.g., 0
 * @property {number} [backOrderedQty] e.g., 0
 * @property {number|null} [lowStockThreshold] e.g., null
 * @property {number|null} [outOfStockThreshold] e.g., null
 */

/**
 * @description
 * @typedef EquinxProductTotalValue
 * @type {object}
 * @property {number} priceAfterDiscount e.g., 2
 * @property {number} originalPrice e.g., 2
 * @property {number} totaldiscount e.g., 0
 * @property {{
 *  CV: { CV: number },
 *  "Regular Price": {
 *    "Regular Price": number
 *  },
 *  PV: { PV: number },
 *  "Wholesale Price": {
 *    "Wholesale Price": number
 *  },
 *  SB: { SB: number },
 * }} priceFacets
 */

/**
 * Standalone types. These are types not related to Product
 * and Equinox Product models.
 * @typedef KitSKUQuantity
 * @type {object}
 * @property {string} sku
 * @property {number} qty
 */
const backOrderDate = require('./backOrderDate');
const availableQuantity = require('./availableQuantity');
const childSKU = require('./childSKU');
const childSKUBundles = require('./childSKUBundles');

module.exports = {
  mapBackOrderDate: backOrderDate,
  mapAvailableQuantity: availableQuantity,
  mapChildSKU: childSKU,
  mapChildSKUBundle: childSKUBundles
};
